/**
 * Essence branch progression (#145, docs/gameplay/skill-definitions.md,
 * Essence Abilities). Casting an Essence ability raises Veil Attunement by
 * the essence it spends; each Attunement milestone grants skill points, and
 * points raise Essence skills ring by ring. Rings live on the skill's level:
 * ring r is level 2r (rings 2, 4 and 5 are levels 4, 8 and 10), and ring 1
 * covers levels 1 to 3. First-pass numbers to tune.
 */

import { findSkillCatalogEntry } from './skill-catalog';
import type { Skill, SkillLevel } from './types/game';

/**
 * Veil Attunement Mastery percentages (0-100) at which a character earns +1 attribute point.
 * Early milestones cluster to reward early engagement; late ones are sparse to gate end-game
 * scaling. See docs/character-attributes-and-stats-plan.md. Lives here, not in skills.ts, so
 * skills.ts can read gear through equipped-items (which imports this module) without a cycle.
 */
export const VEIL_ATTUNEMENT_MILESTONE_PERCENTS: readonly number[] = [5, 10, 15, 20, 30, 40, 50, 65, 80, 100];

/** Essence spent on abilities for each 1% of Veil Attunement. */
export const VEIL_ATTUNEMENT_ESSENCE_PER_PERCENT = 400;
/** Skill points granted by each Veil Attunement milestone. */
export const SKILL_POINTS_PER_MILESTONE = 2;
/** Attribute points granted by each Veil Attunement milestone. */
export const ATTRIBUTE_POINTS_PER_MILESTONE = 1;
export const ESSENCE_RING_MIN = 1;
export const ESSENCE_RING_MAX = 5;
/** Points to raise a skill into each ring: rings 2 and 3 cost 1, ring 4 costs 2, ring 5 costs 3. */
export const ESSENCE_RING_COSTS: Readonly<Record<number, number>> = { 2: 1, 3: 1, 4: 2, 5: 3 };

/**
 * Veil Attunement (0-100%) from the essence a character has spent on abilities.
 * Whole percents only: a percent counts once its full 400 essence is spent.
 */
export function resolveVeilAttunementPercent(essenceSpent: number): number {
    return Math.min(100, Math.floor(Math.max(0, essenceSpent) / VEIL_ATTUNEMENT_ESSENCE_PER_PERCENT));
}

/** Veil Attunement milestones reached at `percent`. */
export function countVeilAttunementMilestones(percent: number): number {
    return VEIL_ATTUNEMENT_MILESTONE_PERCENTS.filter((milestone) => percent >= milestone).length;
}

/** Attribute points earned at `percent` Veil Attunement, beyond those a character is created with. */
export function resolveAttunementAttributePoints(percent: number): number {
    return ATTRIBUTE_POINTS_PER_MILESTONE * countVeilAttunementMilestones(percent);
}

/** Skill points earned at `percent` Veil Attunement. */
export function resolveSkillPointsEarned(percent: number): number {
    return SKILL_POINTS_PER_MILESTONE * countVeilAttunementMilestones(percent);
}

export function isEssenceSkillId(skillId: string): boolean {
    return findSkillCatalogEntry(skillId)?.branch === 'essence';
}

/** An Essence skill's ring (1-5) from its level. */
export function resolveEssenceRing(level: number): number {
    return Math.min(ESSENCE_RING_MAX, Math.max(ESSENCE_RING_MIN, Math.floor(level / 2)));
}

/** The level an Essence skill holds at `ring`: ring 1 stays at level 1. */
export function resolveEssenceRingLevel(ring: number): SkillLevel {
    const clamped = Math.min(ESSENCE_RING_MAX, Math.max(ESSENCE_RING_MIN, Math.trunc(ring)));
    return (clamped === ESSENCE_RING_MIN ? 1 : clamped * 2) as SkillLevel;
}

/** Points it takes to bring a skill from ring 1 to `ring`. */
export function resolveRingPointCost(ring: number): number {
    let total = 0;
    for (let next = ESSENCE_RING_MIN + 1; next <= Math.min(ring, ESSENCE_RING_MAX); next += 1) {
        total += ESSENCE_RING_COSTS[next] ?? 0;
    }
    return total;
}

/** Skill points spent across a character's Essence skills. */
export function resolveSkillPointsSpent(skills: readonly Skill[]): number {
    return skills
        .filter((skill) => isEssenceSkillId(skill.id))
        .reduce((total, skill) => total + resolveRingPointCost(resolveEssenceRing(skill.level)), 0);
}

/** Points free to spend: earned minus spent, never below zero. */
export function resolveSkillPointsAvailable(essenceSpent: number, skills: readonly Skill[]): number {
    return Math.max(0, resolveSkillPointsEarned(resolveVeilAttunementPercent(essenceSpent)) - resolveSkillPointsSpent(skills));
}

/**
 * Why an Essence ring allocation is invalid, or undefined if it's valid: only
 * Essence skills, each a whole ring from 1 to 5, spending no more than
 * `earnedPoints`. Used for a skill-point respec.
 */
export function findEssenceRingAllocationError(rings: Readonly<Record<string, number>>, earnedPoints: number): string | undefined {
    let spent = 0;
    for (const [skillId, ring] of Object.entries(rings)) {
        if (!isEssenceSkillId(skillId)) {
            return `${skillId} is not an Essence skill.`;
        }
        if (!Number.isInteger(ring) || ring < ESSENCE_RING_MIN || ring > ESSENCE_RING_MAX) {
            return `${skillId} must be a whole ring from ${ESSENCE_RING_MIN} to ${ESSENCE_RING_MAX}.`;
        }
        spent += resolveRingPointCost(ring);
    }
    if (spent > earnedPoints) {
        return `That spends ${spent} skill points; you have ${earnedPoints}.`;
    }
    return undefined;
}
